import { Directive, computed, input } from '@angular/core';

import { parseStoryblokEditable } from './parse-storyblok-editable';

/**
 * Marks an element as a Storyblok story block so the Preview Bridge can resolve
 * click-to-edit and highlighting against it.
 *
 * Applied in exactly one place — the top-level body loop in
 * `page.component.html` — so every body component gets annotated without each
 * component having to know about Storyblok. The bridge resolves a click by
 * walking up from `event.target` to the nearest ancestor carrying
 * `data-blok-c`, and `<sirius-section>` is an ancestor of every rendered block,
 * so annotating the section host is enough.
 *
 * Inert outside preview mode: the backend only emits `storyblokEditable` for
 * preview requests, so all three bindings resolve to `null`/`false` and no
 * attribute reaches production markup.
 *
 * Deliberately has no lifecycle hooks and no DOM access, which makes it
 * SSR-safe by construction. It annotates the server-rendered HTML too; that is
 * harmless, because the bridge resolves the attributes at click time rather
 * than scanning the DOM up front.
 */
@Directive({
    selector: '[butterflyStoryblokEditable]',
    host: {
        '[attr.data-blok-c]': 'blokC()',
        '[attr.data-blok-uid]': 'blokUid()',
        '[class.storyblok__outline]': 'isEditable()',
    },
})
export class StoryblokEditableDirective {
    /** Raw Storyblok `_editable` comment from `PageComponentBase.StoryblokEditable`. */
    readonly butterflyStoryblokEditable = input<string | null | undefined>();

    private readonly _attributes = computed(() => parseStoryblokEditable(this.butterflyStoryblokEditable()));

    protected readonly blokC = computed(() => this._attributes()?.['data-blok-c'] ?? null);
    protected readonly blokUid = computed(() => this._attributes()?.['data-blok-uid'] ?? null);
    protected readonly isEditable = computed(() => this._attributes() !== null);
}
